import React, { useEffect, useState, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import Swal from "sweetalert2";
import SignatureField from "../../../components/SignatureField";

const headerFieldSx = {
  "& .MuiInputBase-root": {
    height: 40,
    fontSize: "0.900rem",
    padding: "0 10px",
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  "& .MuiSvgIcon-root": { display: "none" },
};

const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://neuroez4.devforhealth.com/api";

const RehabilitativeProgressNotePreview = ({ data, onClose }) => {
  const [note, setNote] = useState(null);
  const [formMeta, setFormMeta] = useState(null); // full form record
  const [signatures, setSignatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [patientSigLink, setPatientSigLink] = useState("");
  const [openClientSignatureModal, setOpenClientSignatureModal] =
    useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const sigCanvasRef = useRef(null);

  const {
    patientFormId,
    formTitle,
    practiceLogo,
    particeName,
    practiceName: practiceNameProp,
  } = data || {};

  const openClientModal = () => setOpenClientSignatureModal(true);
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;
    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setPatientSigLink(dataUrl);
    setSignatures((prev) => ({ ...prev, client: dataUrl }));
    closeClientModal();
  };

  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) swalEl.style.zIndex = "9999";
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setPatientSigLink("");
        setSignatures((prev) => ({ ...prev, client: "" }));

        try {
          const token = localStorage.getItem(
            "neuro_patientportal_auth_token"
          );
          const client = axios.create({
            baseURL: API_BASE,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          let payload;
          if (formMeta) {
            payload = {
              ...formMeta,
              Id: formMeta.Id ?? formMeta.id ?? patientFormId,
              patientFormId,
              clientsBase64: "",
              clientsURL: "",
              patientSignatureURL: "",
              clientSignatureURL: "",
              imageURLClientSignature: "",
            };
          } else {
            payload = {
              patientFormId,
              clientsBase64: "",
              clientsURL: "",
            };
          }

          await client.post("/Forms/AddPatientForm", payload);

          if (formMeta) {
            setFormMeta((prev) =>
              prev
                ? {
                    ...prev,
                    clientsBase64: "",
                    clientsURL: "",
                    patientSignatureURL: "",
                    clientSignatureURL: "",
                    imageURLClientSignature: "",
                  }
                : prev
            );
          }

          Swal.fire({
            title: "Deleted!",
            text: "Signature has been removed.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) swalEl.style.zIndex = "9999";
            },
          });
        } catch (e) {
          console.error(e);
          Swal.fire({
            title: "Error!",
            text: "Failed to remove signature.",
            icon: "error",
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) swalEl.style.zIndex = "9999";
            },
          });
        }
      }
    });
  };

  const handleSaveForm = async () => {
    if (!patientFormId) return;
    try {
      const token = localStorage.getItem(
        "neuro_patientportal_auth_token"
      );
      const client = axios.create({
        baseURL: API_BASE,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const isBase64 =
        patientSigLink &&
        typeof patientSigLink === "string" &&
        patientSigLink.startsWith("data:");

      let payload;
      if (formMeta) {
        payload = {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? patientFormId,
          patientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
          // NOTE: do not blank clientsURL here
        };
      } else {
        payload = {
          patientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
        };
      }

      await client.post("/Forms/AddPatientForm", payload);

      if (formMeta && isBase64) {
        setFormMeta((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }

      setShowSuccessAlert(true);
    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Error!",
        text: "Failed to save form.",
        icon: "error",
        didOpen: () => {
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) {
            swalEl.style.zIndex = "9999";
          }
        },
      });
    }
  };

  const handleClose = () => {
    setShowSuccessAlert(false);

    if (onClose) {
      onClose();
    } else {
      try {
        const backdrop = document.querySelector(".MuiBackdrop-root");
        if (backdrop) {
          backdrop.click();
        } else {
          const closeButton = document.querySelector('[aria-label="close"]');
          if (closeButton) {
            closeButton.click();
          } else {
            window.history.back();
          }
        }
      } catch (err) {
        console.error("Could not close form:", err);
        window.location.reload();
      }
    }
  };

  const clean = (v) =>
    v == null || String(v).trim() === "" ? "—" : String(v);
  const fmtUsDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };
  const fmtUsDateTime = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    let hh = dt.getHours();
    const mm = String(dt.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;
    return `${fmtUsDate(d)} ${String(hh).padStart(2, "0")}:${mm} ${ampm}`;
  };

  const fmtTime = (v) => {
    if (v == null) return "—";
    if (typeof v === "string") {
      const s = v.trim();
      if (!s) return "—";
      const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/);
      if (m) {
        const hh = m[1].padStart(2, "0");
        const mm = m[2];
        const ap = m[3] ? ` ${m[3].toUpperCase()}` : "";
        return `${hh}:${mm}${ap}`;
      }
    }
    const dt = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(dt.getTime())) return String(v);
    let hh = dt.getHours();
    const mm = String(dt.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;
    return `${String(hh).padStart(2, "0")}:${mm} ${ampm}`;
  };

  const pickNonEmpty = (...vals) => {
    for (const v of vals) {
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
  };
  const joinName = (...parts) =>
    parts.map((p) => (p || "").trim()).filter(Boolean).join(" ");

  const normalizeRowsDiagnostic = (src) => {
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (typeof src === "string") {
      try {
        const parsed = JSON.parse(src);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const extractTextList = (
    val,
    keys = ["text", "value", "label", "name", "title", "objective", "objectives"]
  ) => {
    const out = [];
    const pushIfText = (x) => {
      const s = String(x ?? "").trim();
      if (s) out.push(s);
    };
    const fromAny = (v) => {
      if (v == null) return;
      if (typeof v === "string") {
        try {
          const parsed = JSON.parse(v);
          if (Array.isArray(parsed)) {
            parsed.forEach(pushIfText);
            return;
          }
        } catch {}
        const split = v
          .split(/[\n;|,]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (split.length) split.forEach(pushIfText);
        else pushIfText(v);
        return;
      }
      if (Array.isArray(v)) {
        v.forEach((item) => {
          if (typeof item === "string") fromAny(item);
          else if (item && typeof item === "object") {
            let picked = false;
            for (const k of keys) {
              if (item[k] != null) {
                fromAny(item[k]);
                picked = true;
              }
            }
            if (!picked) {
              Object.values(item).forEach((vv) => {
                if (typeof vv === "string") fromAny(vv);
              });
            }
          }
        });
        return;
      }
      if (typeof v === "object") {
        for (const k of keys) {
          if (v[k] != null) fromAny(v[k]);
        }
        if (!out.length) {
          Object.values(v).forEach((vv) => {
            if (typeof vv === "string") fromAny(vv);
          });
        }
        return;
      }
      pushIfText(v);
    };
    fromAny(val);
    return out;
  };

  const isGoalLike = (r) =>
    r &&
    typeof r === "object" &&
    ("rehabStatus" in r ||
      "rehabobjective" in r ||
      "interventionsStrategies" in r ||
      "interventions" in r ||
      "patientResponce" in r);

  const pickRehabGoalsArray = (noteObj) => {
    const tryNormalize = (src) => normalizeRowsDiagnostic(src) || [];
    const candidates = [
      tryNormalize(noteObj?.rehabGoals),
      tryNormalize(noteObj?.rehabGoal),
      tryNormalize(noteObj?.rehabilitativeGoals),
      tryNormalize(noteObj?.progressNoteGoals),
      tryNormalize(noteObj?.progressGoals),
      tryNormalize(noteObj?.rowsDiagnostic),
      tryNormalize(noteObj?.diagnosticRows),
      tryNormalize(noteObj?.rows),
    ].filter((arr) => Array.isArray(arr) && arr.length);

    for (const arr of candidates) {
      if (arr.some(isGoalLike)) return arr;
    }
    for (const v of Object.values(noteObj || {})) {
      if (Array.isArray(v) && v.length && v.some(isGoalLike)) return v;
    }
    return null;
  };

  const toObjList = (listOrString, key) => {
    if (Array.isArray(listOrString)) return listOrString;
    const lines = extractTextList(listOrString || "");
    return lines.map((txt) => ({ [key]: txt }));
  };

  const loadClientSignature = async (noteData, baseForm, token) => {
    // 1) Base64 first
    const base64Candidate = pickNonEmpty(
      noteData?.clientsBase64,
      baseForm?.clientsBase64,
      noteData?.clientSignatureBase64,
      baseForm?.clientSignatureBase64,
      noteData?.clientBase64,
      baseForm?.clientBase64
    );

    if (
      base64Candidate &&
      typeof base64Candidate === "string" &&
      base64Candidate.toLowerCase().includes("base64")
    ) {
      setSignatures((prev) => ({ ...prev, client: base64Candidate }));
      setPatientSigLink(base64Candidate);
      return;
    }

    // 2) Fallback to URL/path
    const clientPath = pickNonEmpty(
      noteData.clientSignaturePath,
      noteData.clientSignatureURL,
      noteData.clientsURL,
      noteData.patientSignatureURL,
      baseForm.clientSignatureURL,
      baseForm.patientSignatureURL,
      baseForm.clientsURL
    );
    if (!clientPath) return;

    if (/^(https?:|data:|blob:)/i.test(clientPath)) {
      setSignatures((prev) => ({ ...prev, client: clientPath }));
      setPatientSigLink(clientPath);
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
          clientPath
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (ct.includes("text")) {
        const link = (await res.text()).trim();
        if (!link) return;
        setSignatures((prev) => ({ ...prev, client: link }));
        setPatientSigLink(link);
      } else {
        const blob = await res.blob();
        if (blob.size > 0) {
          const objUrl = URL.createObjectURL(blob);
          setSignatures((prev) => ({ ...prev, client: objUrl }));
          setPatientSigLink(objUrl);
        }
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    let active = true;
    if (!patientFormId) {
      setLoading(false);
      setError("Missing patientFormId.");
      return;
    }
    const run = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem(
          "neuro_patientportal_auth_token"
        );
        if (!token) throw new Error("Authentication token missing.");

        const [formRes, noteRes] = await Promise.all([
          fetch(`${API_BASE}/Forms/GetPatientFormsbyId?Id=${patientFormId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(
            `${API_BASE}/Rehabilitative/getProgressNoteById?patientformID=${patientFormId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
        ]);

        if (!formRes.ok || !noteRes.ok) {
          if (noteRes.status === 404 || formRes.status === 404) {
            throw new Error("No forms found for the patient.");
          }
          throw new Error("Failed to load Rehabilitative Progress Note.");
        }

        const [formDataRaw, noteData] = await Promise.all([
          formRes.json(),
          noteRes.json(),
        ]);

        const formData = Array.isArray(formDataRaw)
          ? formDataRaw[0] || {}
          : formDataRaw || {};

        const resolvedClientName = pickNonEmpty(
          noteData?.patientName,
          noteData?.clientName,
          joinName(
            noteData?.firstName,
            noteData?.middleName,
            noteData?.lastName
          ),
          formData?.patientName,
          formData?.clientName,
          joinName(
            formData?.firstName,
            formData?.middleName,
            formData?.lastName
          )
        );

        const merged = {
          ...formData,
          ...noteData,
          __clientName: resolvedClientName,
        };
        if (!merged.patientName || !merged.patientName.trim()) {
          merged.patientName = resolvedClientName;
        }

        if (!active) return;

        setNote(merged);
        setFormMeta(formData || null);
        await loadClientSignature(merged, formData, token);
      } catch (err) {
        if (active) setError(err?.message || "Failed to load Progress Note.");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [patientFormId]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "60vh",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );

  if (!note)
    return (
      <Typography color="textSecondary" align="center" sx={{ p: 3 }}>
        No data available.
      </Typography>
    );

  const headerTitle =
    formTitle || note.customFormTitle || "ARMHS Progress Note";
  const practiceName =
    practiceNameProp || particeName || note.particeName || note.practiceName || "";
  const dosText =
    (note.assessmentDate && fmtUsDate(note.assessmentDate)) ||
    (note.sessionDate && fmtUsDate(note.sessionDate)) ||
    "—";
  const fileNumber =
    note.fileno || note.fileNo || note.accountNo || "—";

  const clientName = pickNonEmpty(
    note.__clientName,
    note.patientName,
    note.clientName,
    data?.patientName,
    data?.clientName,
    joinName(note.firstName, note.middleName, note.lastName),
    joinName(
      note.patientFirstName,
      note.patientMiddleName,
      note.patientLastName
    )
  );

  const dobText = fmtUsDate(note.dateOfBirth || note.dob || null);

  const titleText = pickNonEmpty(
    note.customFormTitle,
    formTitle,
    note.Formtitle,
    headerTitle
  );

  const docStatus = {
    printed: !!(note.isPrinted ?? note.IsPrinted),
    inactive: !!(note.isChecked ?? note.IsChecked),
    audit: !!(note.isAuditReady ?? note.IsAuditReady),
    lock: !!(note.formlock ?? note.formLock),
  };

  const serviceFlags = {
    BLSS: !!note.isBLSS,
    CI: !!note.isCI,
    TCL: !!note.isTCL,
    "MED ED.": !!(note.isMEdED ?? note.isMedED),
    "FA INITIAL": !!note.isFAINITIAL,
    "ITP INITIAL": !!note.isITPINITIAL,
    "FA UPDATE": !!note.isFAUPDATE,
    "ITP UPDATE": !!note.isITPUPDATE,
    INDIVIDUAL: !!note.isINDIVIDUAL,
    GROUP: !!note.isGroup,
  };

  const contactFlags = {
    "Face to Face": !!note.isFaceto,
    Telephone: !!note.isTelephone,
    Other: !!(note.isContactOther ?? note.isOtherContact),
  };

  const locationFlags = {
    "Client's home": !!note.isClienthome,
    Community: !!note.isCommunity,
    Office: !!note.isOffice,
    Telehealth: !!note.isTelehealth,
  };

  const goalRows = pickRehabGoalsArray(note);

  const fallbackSingleRow = {
    rehabStatus: note.rehabStatus ?? note.status ?? "",
    rehabobjective: Array.isArray(note.rehabobjective)
      ? note.rehabobjective
      : toObjList(note.objectivess, "objectives"),
    interventionsStrategies: Array.isArray(note.interventionsStrategies)
      ? note.interventionsStrategies
      : toObjList(note.itpOutlined, "interventions"),
    interventions: note.interventions ?? note.actualInterventions ?? "",
    patientResponce:
      note.patientResponce ??
      note.patientResponse ??
      note.clientResponse ??
      "",
    next: note.next ?? note.nextSteps ?? "",
    observation:
      note.observation ??
      note.observations ??
      note.significantObservations ??
      "",
  };

  const rehabGoals = (
    Array.isArray(goalRows) && goalRows.length ? goalRows : [fallbackSingleRow]
  ).map((row) => {
    const objectivesMeta = Array.isArray(row.rehabobjective)
      ? row.rehabobjective
      : [];
    const outlinedMeta = Array.isArray(row.interventionsStrategies)
      ? row.interventionsStrategies
      : [];
    return {
      status: row.rehabStatus ?? row.status ?? "",
      objectivesMeta,
      outlinedMeta,
      objectives: objectivesMeta.map((o) => o?.objectives).filter(Boolean),
      outlined: outlinedMeta.map((i) => i?.interventions).filter(Boolean),
      interventions: row.interventions ?? row.actualInterventions ?? "",
      response:
        row.patientResponce ?? row.patientResponse ?? row.clientResponse ?? "",
      nextSteps: row.next ?? row.nextSteps ?? "",
      observations:
        row.observation ??
        row.observations ??
        row.significantObservations ??
        "",
    };
  });

  const sigDates = {
    client:
      note.clientSignatureDATE ||
      note.patientSignatureDATE ||
      note.clientDate ||
      null,
    practitioner:
      note.healthPractitionerSignatureDATE ||
      note.practitionerSignatureDATE ||
      note.practitionerDate ||
      note.supervisedClinicianDate ||
      null,
    worker:
      note.rehabWorkerSignatureDATE ||
      note.workerSignatureDATE ||
      note.rehabWorkerDate ||
      note.workerDate ||
      null,
    professional:
      note.providerDate ||
      note.professionalSignatureDATE ||
      note.professionalDate ||
      note.staffsignatureDATE ||
      null,
    director:
      note.treatmentdirectorDate ||
      note.directorDate ||
      note.directorSignatureDATE ||
      null,
  };

  const messageTexts = {
    practitioner:
      note.messagepractitioner ||
      note.messagePractitionerSign ||
      note.messagesupervisedClinician ||
      "",
    worker:
      note.messagerehabWorker ||
      note.messageRehabWorkerSign ||
      note.messageinternSign ||
      "",
    professional: note.message || note.massagestaffSignature || "",
    director: note.messagetreatmentdirector || "",
  };

  const signedFlags = {
    client: Boolean(signatures.client || sigDates.client),
    practitioner: Boolean(
      messageTexts.practitioner ||
        note.practitionerURL ||
        note.practitionerSignatureURL ||
        note.supervisedClinicianURL ||
        sigDates.practitioner
    ),
    worker: Boolean(
      messageTexts.worker ||
        note.rehabWorkerURL ||
        note.workerSignatureURL ||
        sigDates.worker
    ),
    professional: Boolean(
      messageTexts.professional ||
        note.professionalURL ||
        note.professionalSignatureURL ||
        note.internSignURL ||
        note.staffSignatureURL ||
        sigDates.professional
    ),
    director: Boolean(
      messageTexts.director ||
        note.treatmentdirectorURL ||
        note.directorSignatureURL ||
        sigDates.director
    ),
  };

  const buildSignedLine = (role) => {
    const msg = messageTexts[role];
    if (msg && /digitally signed/i.test(msg)) return msg;
    const name =
      (role === "practitioner" && (note.practitionerName || "")) ||
      (role === "worker" && (note.rehabWorkerName || "")) ||
      (role === "professional" && (note.professionalName || "")) ||
      (role === "director" && (note.treatmentdirectorName || "")) ||
      "";
    const date = sigDates[role] ? fmtUsDateTime(sigDates[role]) : "";
    if (name && date) return `Digitally signed by ${name} on ${date}.`;
    if (date) return `Digitally signed on ${date}.`;
    return "__";
  };

  const clientSigUrlRaw = pickNonEmpty(
    signatures.client,
    note.clientSignatureURL,
    note.clientSignURL,
    note.patientSignatureURL,
    note.clientsURL,
    note.clientSignaturePath
  );
  const clientSignedText = sigDates.client
    ? `Signed on ${fmtUsDateTime(sigDates.client)}`
    : null;

  return (
    <>
      {showSuccessAlert && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "300px",
              padding: "20px",
              backgroundColor: "#fff",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              textAlign: "center",
              zIndex: 1300,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Form Saved
            </Typography>
            <Typography style={{ marginBottom: "20px" }}>
              Form saved successfully
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
            >
              OK
            </Button>
          </div>
        </>
      )}

      <Paper
        sx={{
          p: 0,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: "#fff",
            p: 4,
            pb: 0,
            mb: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ position: "absolute", top: 16, left: 16 }}
          >
            <strong>DOS:</strong> {dosText}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              fontWeight: "bold",
            }}
          >
            {headerTitle}
          </Typography>
          {practiceLogo && (
            <Box
              component="img"
              src={practiceLogo}
              alt="Practice Logo"
              sx={{
                width: 65,
                height: 65,
                borderRadius: 1,
                mb: 2,
                mx: "auto",
                display: "block",
              }}
            />
          )}
          <Box sx={{ height: 28 }} />
          <Divider />
        </Box>

        <Box sx={{ px: 4 }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={4}>
              <Typography variant="body2">
                <strong>File #</strong> {clean(fileNumber)}
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ textAlign: "center" }}>
              <Typography variant="body2">
                <strong>Client:</strong> {clean(clientName)}
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ textAlign: "right" }}>
              <Typography variant="body2">
                <strong>DOB:</strong> {dobText}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 3 }} />
        </Box>

        {/* BASIC INFO title + assessment date */}
        <Box sx={{ px: 4, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 4,
              mb: 1,
            }}
          >
            <TextField
              label="Title"
              value={titleText || "—"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              sx={headerFieldSx}
            />
            <TextField
              label="Assessment Date"
              value={fmtUsDate(note.assessmentDate) || "—"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              sx={headerFieldSx}
            />
          </Box>
        </Box>

        <Box sx={{ px: 4 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Document Status
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            <Grid item>
              <FormControlLabel
                control={<Checkbox disabled checked={docStatus.printed} />}
                label="Printed and Filed"
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox disabled checked={docStatus.inactive} />}
                label="Inactive"
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox disabled checked={docStatus.audit} />}
                label="Audit Ready"
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox disabled checked={docStatus.lock} />}
                label="Lock"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Type of Service
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {Object.entries(serviceFlags).map(([label, checked]) => (
              <Grid item xs={6} md={3} key={label}>
                <FormControlLabel
                  control={<Checkbox disabled checked={checked} />}
                  label={label}
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Type of Contact
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {Object.entries(contactFlags).map(([label, checked]) => (
              <Grid item key={label}>
                <FormControlLabel
                  control={<Checkbox disabled checked={checked} />}
                  label={label}
                />
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Service Location
          </Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {Object.entries(locationFlags).map(([label, checked]) => (
              <Grid item key={label}>
                <FormControlLabel
                  control={<Checkbox disabled checked={checked} />}
                  label={label}
                />
              </Grid>
            ))}
          </Grid>

          <TableContainer sx={{ mb: 3 }}>
            <Table
              size="small"
              sx={{ borderCollapse: "collapse", width: "100%" }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      width: "50%",
                    }}
                  >
                    Session Start Time:{" "}
                    {fmtTime(note.sessionStart || note.sessionStartTime)}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #000",
                      fontWeight: "bold",
                      width: "50%",
                    }}
                  >
                    Session End Time:{" "}
                    {fmtTime(note.sessionEnd || note.sessionEndTime)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", fontWeight: "bold" }}>
                    Origination Site:{" "}
                    {clean(note.originationSite || note.origination)}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", fontWeight: "bold" }}>
                    Destination Site:{" "}
                    {clean(note.destinationSite || note.destination)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", fontWeight: "bold" }}>
                    Travel To Start Time:{" "}
                    {fmtTime(note.travelStartTime || note.travelStart)}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", fontWeight: "bold" }}>
                    Travel To End Time:{" "}
                    {fmtTime(note.travelEndTime || note.travelEnd)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: "1px solid #000", fontWeight: "bold" }}>
                    Electronic Source used to calculate:{" "}
                    {clean(note.electronicSource || note.electronic)}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #000", fontWeight: "bold" }}>
                    Mileage: {clean(note.mileage)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {rehabGoals.map((g, idx) => (
            <Box key={`goal-${idx}`} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Rehab Goal{rehabGoals.length > 1 ? ` ${idx + 1}` : ""}:
              </Typography>
              <TableContainer>
                <Table
                  size="small"
                  sx={{ borderCollapse: "collapse", width: "100%" }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: "1px solid #000" }}>
                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          Rehab Status:
                        </p>
                        <p style={{ fontSize: 16 }}>{clean(g.status)}</p>

                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          Objective(s):
                        </p>
                        {Array.isArray(g.objectivesMeta) &&
                        g.objectivesMeta.length > 0 ? (
                          g.objectivesMeta.map((obj, i) => (
                            <div
                              key={`obj-${idx}-${i}`}
                              style={{ marginBottom: 4 }}
                            >
                              <p style={{ fontSize: 16, margin: 0 }}>
                                {clean(obj?.objectives)}
                              </p>
                              {(clean(obj?.timeframe) !== "—" ||
                                clean(obj?.frequency) !== "—") && (
                                <p
                                  style={{
                                    fontSize: 13,
                                    color: "#666",
                                    margin: 0,
                                  }}
                                >
                                  {clean(obj?.timeframe) !== "—"
                                    ? `Timeframe: ${obj.timeframe}`
                                    : ""}
                                  {clean(obj?.timeframe) !== "—" &&
                                  clean(obj?.frequency) !== "—"
                                    ? " • "
                                    : ""}
                                  {clean(obj?.frequency) !== "—"
                                    ? `Frequency: ${obj.frequency}`
                                    : ""}
                                </p>
                              )}
                            </div>
                          ))
                        ) : Array.isArray(g.objectives) &&
                          g.objectives.length > 0 ? (
                          g.objectives.map((line, i) => (
                            <p
                              key={`obj-${idx}-${i}`}
                              style={{ fontSize: 16 }}
                            >
                              {clean(line)}
                            </p>
                          ))
                        ) : (
                          <p style={{ fontSize: 16 }}>—</p>
                        )}

                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          ITP Outlined Interventions:
                        </p>
                        {Array.isArray(g.outlinedMeta) &&
                        g.outlinedMeta.length > 0 ? (
                          g.outlinedMeta.map((it, i) => (
                            <div
                              key={`itp-${idx}-${i}`}
                              style={{ marginBottom: 4 }}
                            >
                              <p style={{ fontSize: 16, margin: 0 }}>
                                {clean(it?.interventions)}
                              </p>
                              {clean(it?.individualsResponsible) !== "—" && (
                                <p
                                  style={{
                                    fontSize: 13,
                                    color: "#666",
                                    margin: 0,
                                  }}
                                >
                                  Responsible:{" "}
                                  {clean(it?.individualsResponsible)}
                                </p>
                              )}
                            </div>
                          ))
                        ) : Array.isArray(g.outlined) &&
                          g.outlined.length > 0 ? (
                          g.outlined.map((line, i) => (
                            <p
                              key={`itp-${idx}-${i}`}
                              style={{ fontSize: 16 }}
                            >
                              {clean(line)}
                            </p>
                          ))
                        ) : (
                          <p style={{ fontSize: 16 }}>—</p>
                        )}

                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          Actual Interventions:
                        </p>
                        <p style={{ fontSize: 16 }}>{clean(g.interventions)}</p>

                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          Client Response:
                        </p>
                        <p style={{ fontSize: 16 }}>{clean(g.response)}</p>

                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          Next Steps:
                        </p>
                        <p style={{ fontSize: 16 }}>{clean(g.nextSteps)}</p>

                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          Significant Observations
                        </p>
                        <p style={{ fontSize: 16 }}>{clean(g.observations)}</p>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Signatures
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table
              size="small"
              sx={{ borderCollapse: "collapse", width: "100%" }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "middle",
                    }}
                  >
                    <Typography>Client</Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "75%",
                      verticalAlign: "middle",
                    }}
                    colSpan={2}
                  >
                    {signedFlags.client ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        {patientSigLink ? (
                          <>
                            <img
                              src={patientSigLink}
                              alt={`Client signature${
                                clientName ? ` for ${clientName}` : ""
                              }`}
                              style={{
                                width: 200,
                                height: 80,
                                border: "2px solid #000",
                                backgroundColor: "#f5f5f5",
                                padding: 10,
                                borderRadius: 8,
                                objectFit: "contain",
                              }}
                            />
                            {clientSignedText && (
                              <Typography
                                sx={{ fontWeight: 700, fontSize: 16 }}
                              >
                                {clientSignedText}
                              </Typography>
                            )}
                            <Tooltip title="Edit Signature" arrow>
                              <Button
                                onClick={openClientModal}
                                variant="outlined"
                                size="small"
                                style={{
                                  minWidth: "32px",
                                  padding: "4px",
                                }}
                                aria-label="Edit Signature"
                              >
                                <EditIcon />
                              </Button>
                            </Tooltip>

                            <Tooltip title="Remove Signature" arrow>
                              <Button
                                onClick={handleDeleteClientSignature}
                                style={{
                                  minWidth: "32px",
                                  padding: "4px",
                                }}
                                aria-label="Remove Signature"
                              >
                                <RemoveCircleOutlineIcon
                                  style={{ color: "red" }}
                                />
                              </Button>
                            </Tooltip>
                          </>
                        ) : (
                          <Link component="button" onClick={openClientModal}>
                            Open signing window
                          </Link>
                        )}
                      </Box>
                    ) : (
                      <Link component="button" onClick={openClientModal}>
                        Open signing window
                      </Link>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography>Mental Health Practitioner</Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "50%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {signedFlags.practitioner
                        ? messageTexts.practitioner &&
                          /digitally signed/i.test(
                            messageTexts.practitioner
                          )
                          ? messageTexts.practitioner
                          : buildSignedLine("practitioner")
                        : "__"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  />
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography>Mental Health Rehab Worker</Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "50%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {signedFlags.worker
                        ? messageTexts.worker &&
                          /digitally signed/i.test(messageTexts.worker)
                          ? messageTexts.worker
                          : buildSignedLine("worker")
                        : "__"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  />
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography>Mental Health Professional</Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "50%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {signedFlags.professional
                        ? messageTexts.professional &&
                          /digitally signed/i.test(
                            messageTexts.professional
                          )
                          ? messageTexts.professional
                          : buildSignedLine("professional")
                        : "__"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  />
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography>ARMHS Treatment Director</Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "50%",
                      verticalAlign: "top",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {signedFlags.director
                        ? messageTexts.director &&
                          /digitally signed/i.test(messageTexts.director)
                          ? messageTexts.director
                          : buildSignedLine("director")
                        : "__"}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: "8px",
                      width: "25%",
                      verticalAlign: "top",
                    }}
                  />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Save Form Button */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 3 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Client Signature Modal */}
      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Client Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
            <SignatureField
              ref={sigCanvasRef}
             
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RehabilitativeProgressNotePreview;